import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle2, PlugZap, Ticket, Workflow } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type ConnectorRow = {
  provider: string
  category: string
  enabled: boolean
  configured: boolean
  last_test_ok?: boolean | null
  last_test_detail?: string | null
  fields_present?: Record<string, boolean>
}

type SdlcSnap = {
  stages: Array<{ id: string; title: string }>
  ticket_providers: string[]
  connectors: ConnectorRow[]
  runs: Array<{
    run_id: string
    status: string
    provider?: string
    ticket_subject?: string
    merge_approved?: boolean
    workforce_run_id?: string
  }>
  safety: Record<string, unknown>
}

type SdlcRun = {
  run_id: string
  status: string
  mission_type?: string
  ticket?: { subject?: string; external_id?: string; provider?: string }
  stages: Array<{ stage_id: string; title: string; status: string; summary?: string }>
  discovery?: { questions?: string[]; summary?: string }
  analysis?: { plan_brief?: string; summary?: string }
  workforce_run_id?: string
  merge_approved?: boolean
  merge_execution_note?: string
  eph_id?: string
  poc?: {
    url?: string
    eph_id?: string
    environment?: string
    note?: string
    collaborate?: { note?: string }
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

const TICKET_PROVIDERS = ['zendesk', 'jira', 'linear', 'freshdesk'] as const

export function TicketsPage() {
  const { user, loading } = useAuth()
  const [params] = useSearchParams()
  const linkedRun = params.get('run')
  const [snap, setSnap] = useState<SdlcSnap | null>(null)
  const [connectors, setConnectors] = useState<ConnectorRow[]>([])
  const [provider, setProvider] = useState<string>('zendesk')
  const [active, setActive] = useState<SdlcRun | null>(null)
  const [secretField, setSecretField] = useState('api_token')
  const [secretValue, setSecretValue] = useState('')
  const [publicSubdomain, setPublicSubdomain] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function refresh() {
    const [sdlc, conn] = await Promise.all([
      api<SdlcSnap>('/api/afos360/v1/sdlc'),
      api<{ connectors: ConnectorRow[] }>('/api/afos360/v1/connectors?scope=project'),
    ])
    setSnap(sdlc)
    setConnectors(conn.connectors)
  }

  useEffect(() => {
    if (user?.customer_id && user?.project_id) {
      void refresh().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
    }
  }, [user?.customer_id, user?.project_id])

  // A link from Approvals or Engineering opens that run directly, even when it is
  // older than the runs listed in the snapshot.
  useEffect(() => {
    if (!linkedRun || !user?.customer_id || !user?.project_id) return
    let cancelled = false
    api<SdlcRun>(`/api/afos360/v1/sdlc/runs/${encodeURIComponent(linkedRun)}`)
      .then((run) => {
        if (!cancelled) setActive(run)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Run not found')
      })
    return () => {
      cancelled = true
    }
  }, [linkedRun, user?.customer_id, user?.project_id])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function onSaveConnector(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const secrets: Record<string, string> = {}
      if (secretValue.trim()) secrets[secretField] = secretValue.trim()
      const publicCfg: Record<string, string> = {}
      if (publicSubdomain.trim()) {
        if (provider === 'zendesk') publicCfg.subdomain = publicSubdomain.trim()
        if (provider === 'freshdesk') publicCfg.domain = publicSubdomain.trim()
        if (provider === 'jira') publicCfg.base_url = publicSubdomain.trim()
      }
      await api('/api/afos360/v1/connectors', {
        method: 'POST',
        body: JSON.stringify({
          provider,
          enabled: true,
          secrets,
          public: publicCfg,
          scope: 'project',
        }),
      })
      const tested = await api<{ ok: boolean; detail: string }>('/api/afos360/v1/connectors/test', {
        method: 'POST',
        body: JSON.stringify({ provider, scope: 'project' }),
      })
      setMessage(`${provider} saved · test ${tested.ok ? 'ok' : 'failed'} (${tested.detail})`)
      setSecretValue('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connector save failed')
    } finally {
      setBusy(false)
    }
  }

  async function onPull() {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ runs: SdlcRun[]; tickets: unknown[] }>(
        '/api/afos360/v1/sdlc/pull',
        {
          method: 'POST',
          body: JSON.stringify({
            provider,
            limit: 2,
            start_sdlc: true,
          }),
        },
      )
      setMessage(
        `Pulled ${result.tickets.length} ticket(s); started ${result.runs.length} SDLC run(s)`,
      )
      if (result.runs[0]) setActive(result.runs[0])
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pull failed')
    } finally {
      setBusy(false)
    }
  }

  async function onApprove() {
    if (!active) return
    setBusy(true)
    setError(null)
    try {
      const run = await api<SdlcRun>(
        `/api/afos360/v1/sdlc/runs/${encodeURIComponent(active.run_id)}/approve-merge`,
        {
          method: 'POST',
          body: JSON.stringify({
            confirm: 'APPROVE_MERGE',
            notes: 'Approved POC after review',
          }),
        },
      )
      setActive(run)
      setMessage('Merge approved (recorded only — no auto-merge to main)')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
        >
          <h1 className="afos-page-title">Tickets → autonomous SDLC</h1>
          <p className="afos-page-lead">
            Wire Zendesk, Jira, Linear, or Freshdesk. Discovery asks questions, a senior agent
            plans, then the workforce builds, tests, deploys a POC, audits delivery — and waits
            for you before merge to main.
          </p>
          {message && <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
        </motion.section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6">
          <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <PlugZap className="h-5 w-5 text-[var(--color-amber)]" />
            Tenant connectors
          </h2>
          <form className="space-y-3" onSubmit={onSaveConnector}>
            <select
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value)
                setSecretField(
                  e.target.value === 'linear' || e.target.value === 'freshdesk'
                    ? 'api_key'
                    : e.target.value === 'jira'
                      ? 'api_token'
                      : 'api_token',
                )
              }}
            >
              {TICKET_PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="subdomain / domain / base_url"
              value={publicSubdomain}
              onChange={(e) => setPublicSubdomain(e.target.value)}
            />
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder={`${secretField} (never shown again)`}
              type="password"
              value={secretValue}
              onChange={(e) => setSecretValue(e.target.value)}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[var(--color-cyan)] px-4 py-3 font-semibold text-[var(--color-void)]"
            >
              Save & test key
            </button>
          </form>
          <div className="space-y-2 text-sm">
            {connectors
              .filter((c) => c.category === 'ticket')
              .map((c) => (
                <div
                  key={c.provider}
                  className="flex items-center justify-between rounded-2xl border border-[var(--color-line)] px-3 py-2"
                >
                  <span>{c.provider}</span>
                  <span className="text-[var(--color-mist)]">
                    {c.configured ? 'configured' : 'empty'}
                    {c.last_test_ok === true
                      ? ' · test ok'
                      : c.last_test_ok === false
                        ? ' · test fail'
                        : ''}
                  </span>
                </div>
              ))}
          </div>
          <p className="text-xs text-[var(--color-mist)]">
            Demo mode: set <code className="text-[var(--color-cyan)]">AFOS360_CONNECTOR_DEMO=1</code>{' '}
            to pull sample tickets without live credentials.
          </p>
        </section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6">
          <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Workflow className="h-5 w-5 text-[var(--color-cyan)]" />
            SDLC loop
          </h2>
          <ol className="space-y-2 text-sm">
            {(snap?.stages || []).map((stage) => (
              <li
                key={stage.id}
                className="rounded-2xl border border-[var(--color-line)] px-3 py-2"
              >
                {stage.title}
              </li>
            ))}
          </ol>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onPull()}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--color-line)] px-4 py-3 font-semibold"
          >
            <Ticket className="h-4 w-4" />
            Pull tickets & run to human gate
          </button>
        </section>

        <section className="space-y-3 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6 lg:col-span-2">
          <h2 className="font-[family-name:var(--font-display)] text-xl font-bold">
            Active run
          </h2>
          {active ? (
            <div className="space-y-3">
              <p className="text-sm text-[var(--color-mist)]">
                {active.run_id} · {active.status} · {active.ticket?.subject}
              </p>
              {active.discovery?.questions && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    Discovery questions
                  </div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
                    {active.discovery.questions.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {active.analysis?.plan_brief && (
                <p className="text-sm text-[var(--color-foam)]">{active.analysis.plan_brief}</p>
              )}
              {active.poc?.url && (
                <div className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 text-sm">
                  <div className="text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    Ephemeral preview (human + agents)
                  </div>
                  <div className="mt-1 font-[family-name:var(--font-mono)] text-[var(--color-cyan)]">
                    {active.poc.eph_id || active.eph_id}
                  </div>
                  <a
                    className="mt-1 block break-all text-[var(--color-foam)] underline-offset-2 hover:underline"
                    href={active.poc.url}
                  >
                    {active.poc.url}
                  </a>
                  <Link
                    to="/ephemeral"
                    className="mt-2 inline-flex text-sm text-[var(--color-cyan)]"
                  >
                    Open ephemeral co-build workspace →
                  </Link>
                  <p className="mt-2 text-[var(--color-mist)]">
                    {active.poc.collaborate?.note || active.poc.note}
                  </p>
                </div>
              )}
              <ol className="grid gap-2 sm:grid-cols-2">
                {active.stages.map((stage) => (
                  <li
                    key={stage.stage_id}
                    className="rounded-2xl border border-[var(--color-line)] px-3 py-2 text-sm"
                  >
                    <div className="flex justify-between gap-2">
                      <strong>{stage.title}</strong>
                      <span className="text-[var(--color-cyan)]">{stage.status}</span>
                    </div>
                    {stage.summary && (
                      <p className="mt-1 text-[var(--color-mist)]">{stage.summary}</p>
                    )}
                  </li>
                ))}
              </ol>
              {active.status === 'awaiting_human' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onApprove()}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--color-foam)] px-4 py-3 font-semibold text-[var(--color-void)]"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve merge (record only)
                </button>
              )}
              {active.merge_approved && (
                <p className="text-sm text-[var(--color-cyan)]">
                  {active.merge_execution_note}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-mist)]">
              Pull tickets to start a governed SDLC run.
            </p>
          )}
          <div className="grid gap-2 sm:grid-cols-3">
            {(snap?.runs || []).map((run) => (
              <button
                key={run.run_id}
                type="button"
                className="rounded-2xl border border-[var(--color-line)] px-3 py-3 text-left text-sm"
                onClick={() => {
                  void api<SdlcRun>(
                    `/api/afos360/v1/sdlc/runs/${encodeURIComponent(run.run_id)}`,
                  ).then(setActive)
                }}
              >
                <div className="text-[var(--color-cyan)]">{run.provider}</div>
                <div className="truncate">{run.ticket_subject}</div>
                <div className="text-xs text-[var(--color-mist)]">
                  {run.status}
                  {run.merge_approved ? ' · merge approved' : ''}
                </div>
              </button>
            ))}
          </div>
        </section>
      </main>
    </AppChrome>
  )
}
